import React from 'react';
import { useJobs } from 'src/hooks/useJobs';

import { Alert } from 'src/design/atoms/Alert';
import { Card } from 'src/design/atoms/Card';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Progress } from 'src/design/atoms/Progress';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { IconFilter, IconFilterOff } from '@tabler/icons-react';
import {
  MilestonePhaseDto,
  MilestonePhaseItemDto,
} from 'src/api/FiretrolApiClient';
import { useJobMilestonePhases } from 'src/api/queries/milestones/useMilestonePhases';
import { useMilestoneItems } from 'src/api/queries/milestones/useMilestoneItems';
import { useDeleteMilestonePhase } from 'src/api/queries/milestones/useMilestonePhaseMutations';
import { MilestonePhaseDialog } from 'src/app/dialogs/MilestonePhaseDialog';
import { MilestonePhaseItemDialog } from 'src/app/dialogs/MilestonePhaseItemDialog/MilestonePhaseItemDialog';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import { getErrorMessage } from 'src/utils';
import { MilestonePhaseItemTable } from './MilestonePhaseItemTable';

export const Milestones: React.FC = () => {
  const { selectedJob } = useJobs();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [phaseDialogOpen, setPhaseDialogOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] =
    React.useState<MilestonePhaseItemDto | null>(null);
  const [copySource, setCopySource] =
    React.useState<MilestonePhaseItemDto | null>(null);
  const [selectedPhase, setSelectedPhase] =
    React.useState<MilestonePhaseDto | null>(null);
  const [selectedPhaseId, setSelectedPhaseId] = React.useState<string | null>(
    null,
  );

  const phasesQuery = useJobMilestonePhases(selectedJob?.id);
  const itemsQuery = useMilestoneItems(selectedJob?.id, selectedPhaseId);
  const deletePhase = useDeleteMilestonePhase();

  const phases = phasesQuery.data ?? [];
  const items = itemsQuery.data ?? [];
  const loading = phasesQuery.isLoading || itemsQuery.isLoading;
  const error = phasesQuery.error ?? itemsQuery.error;

  const remove = (id: string) => {
    if (!selectedJob) return;
    deletePhase.mutate(
      { scope: { jobId: selectedJob.id }, phaseId: id },
      { onSuccess: () => setSelectedPhaseId(null) },
    );
  };

  const handleCreatePhase = () => {
    setSelectedPhase(null);
    setPhaseDialogOpen(true);
  };

  const handleEditPhase = (phase: MilestonePhaseDto) => {
    setSelectedPhase(phase);
    setPhaseDialogOpen(true);
  };

  const renderPhases = () => {
    return phases.map((phase) => {
      const completedItems = phase.targetItems.filter((item) => item.complete);
      const percentage =
        phase.targetItems.length === 0
          ? 0
          : (completedItems.length / phase.targetItems.length) * 100;
      const isSelected = selectedPhaseId === phase.id;
      return (
        <Grid.Col span={{ base: 12, md: 3 }} key={phase.id}>
          <Card>
            <Stack>
              <Title order={5}>
                {phase.title} - {Math.round(percentage)}% complete
              </Title>
              <Progress value={percentage} />
              <Group justify="flex-end">
                <BasicActionIcon
                  color={isSelected ? 'green' : 'blue'}
                  onClick={() =>
                    isSelected
                      ? setSelectedPhaseId(null)
                      : setSelectedPhaseId(phase.id)
                  }
                >
                  {isSelected ? (
                    <IconFilter size={18} />
                  ) : (
                    <IconFilterOff size={18} />
                  )}
                </BasicActionIcon>
                <EditActionIcon onClick={() => handleEditPhase(phase)} />
                <DeleteActionIcon
                  requiredConfirmation={true}
                  confirmationTitle="Are you sure you want to remove this phase?"
                  confirmationText="All checklist items for this phase will also be removed. This action cannot be reversed."
                  onDelete={() => remove(phase.id)}
                />
              </Group>
            </Stack>
          </Card>
        </Grid.Col>
      );
    });
  };
  if (!selectedJob) return null;
  return (
    <div>
      <Header
        title="Milestones"
        helpText="Manage the milestones for the job"
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            { text: 'Milestones', href: '' },
          ],
        }}
      />
      {error && <Alert color="red">{getErrorMessage(error)}</Alert>}
      <Grid mb="md">
        {renderPhases()}

        {phases.length === 0 ? (
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Group>
              <CreateButton onClick={handleCreatePhase}>
                Add New Phase
              </CreateButton>
            </Group>
          </Grid.Col>
        ) : (
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Group justify="center" align="center" mt="xl">
              <CreateButton onClick={handleCreatePhase}>
                Add New Phase
              </CreateButton>
            </Group>
          </Grid.Col>
        )}
      </Grid>
      <MilestonePhaseItemTable
        jobId={selectedJob.id}
        data={items}
        onRowClick={(item) => setSelectedItem(item)}
        onCopyClick={(item) => setCopySource(item)}
        onCreateClick={() => setDialogOpen(true)}
        loading={loading}
      />
      <MilestonePhaseItemDialog
        selectedJobId={selectedJob.id}
        opened={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
      <MilestonePhaseDialog
        isOpen={phaseDialogOpen}
        onClose={() => setPhaseDialogOpen(false)}
        jobId={selectedJob.id}
        data={selectedPhase}
      />
      {selectedItem && (
        <MilestonePhaseItemDialog
          data={selectedItem}
          opened={selectedItem !== null}
          onClose={() => setSelectedItem(null)}
          selectedJobId={selectedJob.id}
        />
      )}
      {copySource && (
        <MilestonePhaseItemDialog
          copyFrom={copySource}
          opened
          onClose={() => setCopySource(null)}
          selectedJobId={selectedJob.id}
        />
      )}
    </div>
  );
};
